import { checkGstin, normaliseGstin } from '@oneapp/gst';
import type { BusinessRole, GstConnectionStatus, RegistrationStatus, Taxpayer } from '@oneapp/shared';
import { type ReactNode, type Ref, useId } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon, type IconName } from '../components/icons';
import { Badge } from '../components/ui';

export const STATUS_TONE = {
  active: 'green',
  cancelled: 'red',
  suspended: 'amber',
  inactive: 'amber',
} as const satisfies Record<RegistrationStatus, string>;

/** "26 Sep 2026" / "26 सित॰ 2026" from an ISO date or timestamp. */
/** "14 Sept": the date without the year when it's this year, else with it. */
export function useShortDate() {
  const { i18n } = useTranslation();
  return (iso: string) => {
    const date = new Date(iso.length === 10 ? `${iso}T00:00:00` : iso);
    return date.toLocaleDateString(i18n.language === 'hi' ? 'hi-IN' : 'en-IN', {
      day: 'numeric',
      month: 'short',
      ...(date.getFullYear() === new Date().getFullYear() ? {} : { year: 'numeric' }),
    });
  };
}

export function useDate() {
  const { i18n } = useTranslation();
  return (iso: string) =>
    new Date(iso.length === 10 ? `${iso}T00:00:00` : iso).toLocaleDateString(
      i18n.language === 'hi' ? 'hi-IN' : 'en-IN',
      {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      },
    );
}

/** "4:58 pm" from an ISO time, in the person's language: India's time, as the portal and the chat give it. */
export function useTime() {
  const { i18n } = useTranslation();
  return (iso: string) =>
    new Date(iso).toLocaleTimeString(i18n.language === 'hi' ? 'hi-IN' : 'en-IN', {
      hour: 'numeric',
      minute: '2-digit',
      timeZone: 'Asia/Kolkata',
    });
}

/** A person's GST portal connection for a business, as a badge (lists, the chat's bar, the business page). */
export function ConnectionBadge({ status }: { status: GstConnectionStatus }) {
  const { t } = useTranslation();
  if (status === 'connected') return <Badge tone="green">{t('businesses.connected')}</Badge>;
  if (status === 'reconnect_needed') return <Badge tone="amber">{t('businesses.reconnectNeeded')}</Badge>;
  return <Badge>{t('businesses.notConnected')}</Badge>;
}

/**
 * The GSTIN box (G1): capitals as you type, checked as you go (format, state code, check character),
 * with the likely fix to tap when one character looks mistyped. Nothing is sent anywhere to check it.
 */
export function GstinField({
  value,
  onChange,
  inputRef,
  label,
  found,
  error,
  placeholder = '29AAKPS1234Q1Z0',
}: {
  value: string;
  onChange: (value: string) => void;
  inputRef?: Ref<HTMLInputElement>;
  /** Defaults to "GSTIN". */
  label?: string;
  /** Shown instead of the state once it's valid, e.g. the name from the register. */
  found?: ReactNode;
  /** A problem found elsewhere (the server), shown when the GSTIN itself looks fine. */
  error?: string | null;
  /** An example GSTIN (not the person's own, for a buyer's). */
  placeholder?: string;
}) {
  const { t } = useTranslation();
  const id = useId();
  const helpId = `${id}-help`;
  const typed = normaliseGstin(value);
  const check = typed ? checkGstin(typed) : null;
  // A half-typed GSTIN isn't wrong yet: judge it once it's full length (or valid).
  const judged = check && (check.ok || typed.length >= 15);
  let help: ReactNode = null;
  if (check?.ok && error) {
    help = <p className="field__help field__help--error">{error}</p>;
  } else if (check?.ok) {
    help = found ?? (
      <p className="field__help field__help--ok">
        <Icon name="check-circle" size={16} />
        {t('businesses.addPage.valid', { state: check.stateName })}
      </p>
    );
  } else if (judged && check && !check.ok) {
    const suggestions = check.suggestions;
    help = (
      <div className="gstin-help">
        <p className="field__help field__help--error">
          {t(`businesses.addPage.problem.${check.problem}`, { count: typed.length, code: typed.slice(0, 2) })}
        </p>
        {suggestions.length ? (
          <>
            <p className="field__help">
              {t('businesses.addPage.didYouMean', { count: suggestions.length, gstin: suggestions[0] })}
            </p>
            <div className="gstin-help__fixes">
              {suggestions.map((gstin) => (
                <button key={gstin} type="button" className="chip chip--mono" onClick={() => onChange(gstin)}>
                  {t('businesses.addPage.use', { gstin })}
                </button>
              ))}
            </div>
          </>
        ) : null}
      </div>
    );
  } else if (error) {
    // Nothing to judge yet (empty or half-typed), but it was needed.
    help = <p className="field__help field__help--error">{error}</p>;
  }
  return (
    <div className="field">
      <label className="field__label" htmlFor={id}>
        {label ?? t('businesses.addPage.field')}
      </label>
      <input
        id={id}
        ref={inputRef}
        className="input input--mono"
        value={value}
        onChange={(event) => onChange(event.target.value.toUpperCase())}
        autoComplete="off"
        autoCapitalize="characters"
        spellCheck={false}
        maxLength={24}
        placeholder={placeholder}
        aria-invalid={(judged && check && !check.ok) || error ? true : undefined}
        aria-describedby={helpId}
      />
      <div id={helpId} aria-live="polite">
        {help}
      </div>
    </div>
  );
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="fields__row">
      <dt>{label}</dt>
      <dd>{children}</dd>
    </div>
  );
}

/** A registration as the public GST register shows it (G3). */
export function TaxpayerCard({ taxpayer, children }: { taxpayer: Taxpayer; children?: ReactNode }) {
  const { t } = useTranslation();
  const titleId = useId();
  const date = useDate();
  const type = [taxpayer.taxpayerType, taxpayer.filingFrequency ? t(`businesses.${taxpayer.filingFrequency}`) : null]
    .filter(Boolean)
    .join(' · ');
  return (
    <section className="card card--chat" aria-labelledby={titleId}>
      <div className="card__head">
        <span className="icon-tile">
          <Icon name="building" />
        </span>
        <div className="card__titles">
          <h2 className="card__title" id={titleId}>
            {taxpayer.tradeName ?? taxpayer.legalName}
          </h2>
          <span className="card__subtitle">{t('businesses.confirm.fromRegister')}</span>
        </div>
      </div>
      <div className="card__section">
        <dl className="fields">
          <Row label={t('businesses.page.legalName')}>{taxpayer.legalName}</Row>
          <Row label={t('businesses.page.gstin')}>
            <span className="mono">{taxpayer.gstin}</span>
          </Row>
          <Row label={t('businesses.page.state')}>{taxpayer.stateName}</Row>
          {type ? <Row label={t('businesses.page.type')}>{type}</Row> : null}
          {taxpayer.constitution ? <Row label={t('businesses.page.constitution')}>{taxpayer.constitution}</Row> : null}
          <Row label={t('businesses.page.status')}>
            <Badge tone={STATUS_TONE[taxpayer.status]}>{t(`businesses.status.${taxpayer.status}`)}</Badge>
          </Row>
          {taxpayer.status === 'cancelled' && taxpayer.cancelledOn ? null : taxpayer.registeredOn ? (
            <Row label={t('businesses.page.registeredOn')}>{date(taxpayer.registeredOn)}</Row>
          ) : null}
        </dl>
      </div>
      {children}
    </section>
  );
}

const ROLES: { id: BusinessRole; icon: IconName }[] = [
  { id: 'owner', icon: 'user' },
  { id: 'staff', icon: 'briefcase' },
];

/** "Your role in this business" (G3). */
export function RolePicker({ value, onChange }: { value: BusinessRole; onChange: (role: BusinessRole) => void }) {
  const { t } = useTranslation();
  const name = useId();
  return (
    <fieldset className="choice-cards">
      <legend className="section-label">{t('businesses.confirm.role')}</legend>
      {ROLES.map((role) => (
        <label key={role.id} className={`choice-card${value === role.id ? ' choice-card--on' : ''}`}>
          <span className="icon-tile">
            <Icon name={role.icon} />
          </span>
          <span className="choice-card__text">
            <span className="choice-card__label">{t(`businesses.confirm.${role.id}Label`)}</span>
            <span className="choice-card__help">{t(`businesses.confirm.${role.id}Help`)}</span>
          </span>
          <input
            type="radio"
            name={name}
            value={role.id}
            checked={value === role.id}
            onChange={() => onChange(role.id)}
          />
        </label>
      ))}
    </fieldset>
  );
}
